import * as THREE from 'three';

export function createUDIMAtlas() {
    if (this.uvChannels.length === 0) return;

    const baseColor = this.getSelectedBaseSkinColor ? this.getSelectedBaseSkinColor() : '#f4d1ae';
    this.uvChannels.forEach((channel) => {
        const ctx = channel.baseContext;
        ctx.save();
        ctx.globalCompositeOperation = 'source-over';
        ctx.fillStyle = baseColor;
        ctx.fillRect(0, 0, channel.baseCanvas.width, channel.baseCanvas.height);
        ctx.restore();
    });

    if (this.atlasTexture) { this.atlasTexture.dispose(); this.atlasTexture = null; }
    if (this.atlasMaterial) { this.atlasMaterial.dispose(); this.atlasMaterial = null; }

    let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity;
    this.uvChannels.forEach(channel => {
        const tile = channel.udimTile;
        const tileU = (tile - 1001) % 10;
        const tileV = Math.floor((tile - 1001) / 10);
        minU = Math.min(minU, tileU); maxU = Math.max(maxU, tileU);
        minV = Math.min(minV, tileV); maxV = Math.max(maxV, tileV);
    });

    const res = this.textureResolution || 512;
    const atlasWidth = (maxU - minU + 1) * res;
    const atlasHeight = (maxV - minV + 1) * res;

    const atlasCanvas = document.createElement('canvas');
    atlasCanvas.width = atlasWidth; atlasCanvas.height = atlasHeight;
    const atlasCtx = atlasCanvas.getContext('2d', { willReadFrequently: true });
    atlasCtx.fillStyle = baseColor;
    atlasCtx.fillRect(0, 0, atlasWidth, atlasHeight);

    this.uvChannels.forEach(channel => {
        const tile = channel.udimTile;
        const tileU = (tile - 1001) % 10;
        const tileV = Math.floor((tile - 1001) / 10);
        const x = (tileU - minU) * res;
        const y = (tileV - minV) * res;
        atlasCtx.drawImage(channel.compositeCanvas, x, y, res, res);
    });

    this.atlasTexture = new THREE.CanvasTexture(atlasCanvas);
    Object.assign(this.atlasTexture, {
        generateMipmaps: false, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter,
        format: THREE.RGBAFormat, flipY: false
    });

    this.atlasMaterial = new THREE.ShaderMaterial({
        uniforms: {
            atlasTexture: { value: this.atlasTexture },
            minU: { value: minU }, minV: { value: minV },
            atlasScaleU: { value: maxU - minU + 1 }, atlasScaleV: { value: maxV - minV + 1 }
        },
        vertexShader: this.atlasVertexShader || null,
        fragmentShader: this.atlasFragmentShader || null,
        transparent: true, side: THREE.DoubleSide
    });

    if (!this.atlasMaterial.vertexShader || !this.atlasMaterial.fragmentShader) {
        this.atlasMaterial.dispose();
        this.atlasMaterial = new THREE.MeshBasicMaterial({ map: this.atlasTexture, side: THREE.DoubleSide });
    }

    this.mesh.traverse(child => {
        if (child.isMesh) {
            if (child.material && child.material !== this.atlasMaterial) {
                if (Array.isArray(child.material)) {
                    child.material.forEach(m => m && typeof m.dispose === 'function' && m.dispose());
                } else if (typeof child.material.dispose === 'function') {
                    child.material.dispose();
                }
            }
            child.material = this.atlasMaterial;
        }
    });
}

export function applyUDIMTextures() {
    if (!this.mesh || this.uvChannels.length === 0) return;
    this.createUDIMAtlas();
}

export function refreshCharacterMaterials() {
    if (!this.mesh || this.uvChannels.length === 0) {
        this.updateStatus?.('No model or textures available to refresh');
        return;
    }

    try {
        const baseColor = this.getSelectedBaseSkinColor ? this.getSelectedBaseSkinColor() : '#f4d1ae';
        this.uvChannels.forEach(channel => {
            if (!channel || !channel.baseContext) return;
            channel.baseContext.save();
            channel.baseContext.globalCompositeOperation = 'source-over';
            channel.baseContext.fillStyle = baseColor;
            channel.baseContext.fillRect(0, 0, channel.baseCanvas.width, channel.baseCanvas.height);
            channel.baseContext.restore();
        });

        this.uvChannels.forEach((channel, index) => {
            if (channel) this.compositeTexture(index);
        });
        this.applyUDIMTextures();
    } catch (error) {
        console.error('Error refreshing character materials:', error);
        this.updateStatus?.('Error refreshing character materials: ' + error.message);
    }
}
